import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, TrendingUp, Award, Search, Mail, Download, MessageCircle, Calendar, Clock, ArrowRight, Zap, FileBarChart, Send, HelpCircle, CheckCircle, AlertCircle, Activity, Filter, ExternalLink, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { AIInsightPopover } from '../components/AIInsightPopover';
export function AdminHub() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [reminderLearner, setReminderLearner] = useState('');
  const [isReminderDropdownOpen, setIsReminderDropdownOpen] = useState(false);
  const [timePeriod, setTimePeriod] = useState<'7d' | '30d'>('30d');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const reminderDropdownRef = useRef<HTMLDivElement>(null);
  const incompleteLearners = [{
    value: 'rachel-patel',
    name: 'Rachel Patel',
    email: 'rachel.patel@example.com'
  }, {
    value: 'david-nguyen',
    name: 'David Nguyen',
    email: 'd.nguyen@example.com'
  }, {
    value: 'karen-brooks',
    name: 'Karen Brooks',
    email: 'k.brooks@example.com'
  }, {
    value: 'marcus-hall',
    name: 'Marcus Hall',
    email: 'marcus.h@example.com'
  }, {
    value: 'tanya-foster',
    name: 'Tanya Foster',
    email: 'tanya.f@example.com'
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (reminderDropdownRef.current && !reminderDropdownRef.current.contains(event.target as Node)) {
        setIsReminderDropdownOpen(false);
      }
    };
    if (isUserMenuOpen || isReminderDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isReminderDropdownOpen]);
  const navigationCards = [{
    title: 'Analytics Dashboard',
    description: 'View detailed performance metrics and insights',
    icon: BarChart3,
    color: 'from-[#00638C] to-[#30718D]',
    stats: '248 learners tracked',
    link: '/analytics'
  }, {
    title: 'User Management',
    description: 'Manage learner accounts and permissions',
    icon: UserCog,
    color: 'from-[#30718D] to-[#265a70]',
    stats: '18 pending invites',
    link: '/assessment'
  }, {
    title: 'Reports',
    description: 'Generate and download custom reports',
    icon: FileText,
    color: 'from-[#007558] to-[#005a44]',
    stats: 'Last report: 1 hour ago',
    link: '/feedback'
  }, {
    title: 'Insights & Actions',
    description: 'Prioritize learner support with AI-powered skill insights',
    icon: Zap,
    color: 'from-[#DB3725] to-[#DB3725]',
    stats: '4 priority insights',
    link: '/insights-actions'
  }];
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1763" data-mp="2heb8ysixi3s7">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1764" data-mp="1kplmeowcwmzw">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1765" data-mp="1oq5irq93vmkz">
          <div className="flex items-center h-16" data-id="element-1766" data-mp="1os89kci31jqr">
            {/* Logo */}
            <div className="text-[#E53E3E] font-bold text-xl" data-id="element-1767" data-mp="1togsm0zehx37">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-1768" data-mp="1ypwe1u5hq5m4">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-[#00638C] border-b-2 border-[#00638C] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-1769" data-mp="353cbjz52ykpk">
                <Home size={18} data-id="element-1770" data-mp="hvy44lgaxw5k" />
                <span className="hidden sm:inline" data-id="element-1771" data-mp="hwzqhm2rq52l">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1772" data-mp="vvzecrigifxp">
                <BarChart3 size={18} data-id="element-1773" data-mp="15s2ejrtr04i0" />
                <span className="hidden sm:inline" data-id="element-1774" data-mp="10v283fszkzqd">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1775" data-mp="10szhatk0f2kl">
                <FileText size={18} data-id="element-1776" data-mp="1ojlic0vljml3" />
                <span className="hidden sm:inline" data-id="element-1777" data-mp="1oko4jvhz50uo">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1778" data-mp="1jmv1hbbywmym">
                <UserCog size={18} data-id="element-1779" data-mp="1t8xo8qinfr6v" />
                <span className="hidden sm:inline" data-id="element-1780" data-mp="1t9pavbu9qel0">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-1781" data-mp="1ya3gkn8edcfv">
              <Link data-phoebe-nav="/alerts" to="/alerts" aria-label="Notifications" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1782" data-mp="1kfu3ry9l6e2q">
                <Bell size={20} data-id="element-1783" data-mp="1fhisrk7k1xix" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1784" data-mp="1fgpwjd682qf1"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1785" data-mp="11j17ru91az4p">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1786" data-mp="15j3bsa28s4n5">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1787" data-mp="33e8sbuzeu3sa">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-1788" data-mp="2thm735369apq">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1789" data-mp="3r434eabwnobh" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1790" data-mp="3h6olm42ieng0">
                    <div className="py-2" data-id="element-1791" data-mp="3d5uyhcww5co6">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1792" data-mp="3d763th5zishe">
                        <Users size={18} className="text-gray-600" data-id="element-1793" data-mp="h7vw9419j7qy" />
                        <span className="font-medium text-gray-900" data-id="element-1794" data-mp="h9qfmdwvyrsx">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1795" data-mp="q8bxs7yg44t6">
                        <Home size={18} className="text-gray-600" data-id="element-1796" data-mp="v1ddgi6h88dt" />
                        <span className="font-medium text-gray-900" data-id="element-1797" data-mp="zxbz3y0f7caj">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1798" data-mp="18zbgwsj9ekej">
                        <UserCog size={18} className="text-gray-600" data-id="element-1799" data-mp="1dwv0m7yq7pdv" />
                        <span className="font-medium text-gray-900" data-id="element-1800" data-mp="3ar7zun7rawjc">
                          Admin Hub
                        </span>
                      </button>
                      <div className="border-t border-gray-200 my-2" data-id="element-1801" data-mp="3frcfkb0ubn8i"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1802" data-mp="1lfun4mhhhx1g">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1803" data-mp="1v77cn7ft8b4b">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1804" data-mp="1ciair4obrkmm"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1805" data-mp="17h4q16unzxd3"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1806" data-mp="2nvmnrivtm9ad"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1807" data-mp="2ixkz8ifmlhri">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1808" data-mp="3rvkppy1ootvq">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-1809" data-mp="oi1nfh573pq">
          <div data-id="element-1810" data-mp="oekonh679jhh">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#30718D] mb-1" data-id="element-1811" data-mp="oe2v4sax9jd9">
              Administrator
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-1812" data-mp="enpza3xnq3rt">
              Welcome Back, Linda
            </h1>
          </div>
          <button className="text-[#DB3725] hover:text-[#c02f1f] font-medium text-sm transition-colors self-start sm:self-auto flex items-center gap-1.5" data-id="element-1813" data-mp="jlj8l5u9bdwb">
            <Mail size={14} data-id="element-1814" data-mp="1c2vjo3p7soxu" />
            Contact support
          </button>
        </div>

        {/* Company & Send Email Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-1815" data-mp="q23iwqnunpo1">
          {/* Company Info Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.05
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex flex-col" data-phoebe-tour="company-info" data-id="element-1816" data-mp="uz3luspekcax">
            <div className="flex items-center gap-3 mb-4" data-id="element-1817" data-mp="c22ex2cag24c">
              <div className="w-8 h-8 rounded-lg bg-[#30718D]/10 flex items-center justify-center flex-shrink-0" data-id="element-1818" data-mp="3ti250rz75tfa">
                <Building2 size={16} className="text-[#30718D]" data-id="element-1819" data-mp="2aprqo1fq04n" />
              </div>
              <div data-id="element-1820" data-mp="3kgkjgu30szj7">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1821" data-mp="3ki5dmilfaggj">
                  Company Name
                </div>
                <div className="text-base font-semibold text-gray-900" data-id="element-1822" data-mp="3pi05opgxpc36">
                  Lorem Ipsum
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100" data-id="element-1823" data-mp="30wlyfnoi2uu9">
              <div className="flex items-center gap-3" data-id="element-1824" data-mp="3eutu7s9svq3u">
                <div className="w-8 h-8 rounded-lg bg-[#007558]/10 flex items-center justify-center flex-shrink-0" data-id="element-1825" data-mp="3eweodgs7d716">
                  <Users size={16} className="text-[#007558]" data-id="element-1826" data-mp="3avur8d7mq3f1" />
                </div>
                <div data-id="element-1827" data-mp="3oreuk0avkd1c">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1828" data-mp="3ju6ec59aej2q">
                    Registered Learners
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-1829" data-mp="2d5csa1ga39j">
                    4 Learners
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-3" data-id="element-1830" data-mp="bf4smz0qb1aw">
              <div className="flex items-center gap-3" data-id="element-1831" data-mp="bca5gzqbz2ds">
                <div className="w-8 h-8 rounded-lg bg-[#DB3725]/10 flex items-center justify-center flex-shrink-0" data-id="element-1832" data-mp="6gbdz39ph495">
                  <Clock size={16} className="text-[#DB3725]" data-id="element-1833" data-mp="308t9xb003fcd" />
                </div>
                <div data-id="element-1834" data-mp="3574i7handhsh">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1835" data-mp="356kxjstm4far">
                    Pending
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-1836" data-mp="3dz6gpa15oo04">
                    5 Learners
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Send Account Creation Email */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2" data-phoebe-tour="send-email" data-id="element-1837" data-mp="3nyxatalt10zo">
            <div className="flex items-center gap-3 mb-4" data-id="element-1838" data-mp="22lkiz0h8de2z">
              <div className="w-10 h-10 rounded-lg bg-[#30718D]/10 flex items-center justify-center" data-id="element-1839" data-mp="1v10bno1yn1uq">
                <Mail size={20} className="text-[#30718D]" data-id="element-1840" data-mp="1q8gpw3t4d0eu" />
              </div>
              <div data-id="element-1841" data-mp="1l9dwxhqeykn8">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1842" data-mp="1layr368tg1kk">
                  Send Account Creation Email
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1843" data-mp="1h6hath1o06ej">
                  Remind learners to complete account setup.{' '}
                  <span className="font-semibold text-[#DB3725]" data-id="element-1844" data-mp="28zzqy53l24e8">
                    5 pending
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-3 items-end" data-id="element-1845" data-mp="2509246xm9ycc">
              <div className="relative flex-1" ref={reminderDropdownRef} data-id="element-1846" data-mp="1zy09xcskj0ba">
                <button onClick={() => {
                setIsReminderDropdownOpen(!isReminderDropdownOpen);
              }} className="flex items-center gap-2 w-full px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-medium" data-id="element-1847" data-mp="tipsnwtg0z5c">
                  <Users size={18} className="text-[#30718D]" data-id="element-1848" data-mp="2h8vt1q11v9un" />
                  <span className="flex-1 text-left text-gray-700" data-id="element-1849" data-mp="23gfwn82nma2p">
                    {reminderLearner ? incompleteLearners.find(l => l.value === reminderLearner)?.name : 'All incomplete learners (5)'}
                  </span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isReminderDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1850" data-mp="304vppe4z31kk" />
                </button>

                {isReminderDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto" data-id="element-1851" data-mp="1aqpiiz10v86l">
                    <button onClick={() => {
                  setReminderLearner('');
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm ${reminderLearner === '' ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-1852" data-mp="1fo8u1r6c56uu">
                      All incomplete learners
                    </button>
                    {incompleteLearners.map(learner => <button key={learner.value} onClick={() => {
                  setReminderLearner(learner.value);
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${reminderLearner === learner.value ? 'bg-[#30718D]/5' : ''}`} data-id="element-1853" data-mp="jl1tblo6hnai">
                        <div className={`font-medium text-sm ${reminderLearner === learner.value ? 'text-[#30718D]' : 'text-gray-900'}`} data-id="element-1854" data-mp="20uff4urovg00">
                          {learner.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1855" data-mp="2emvmg7rmi06q">
                          {learner.email}
                        </div>
                      </button>)}
                  </div>}
              </div>
              <button className="px-4 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-1856" data-mp="1mwiki3wspjnz">
                <Send size={16} data-id="element-1857" data-mp="1vwx7d7per6tl" />
                Send
              </button>
            </div>
          </motion.div>
        </div>

        {/* Performance Snapshot */}
        <div data-phoebe-tour="dashboard" className="mb-8" data-id="element-1858" data-mp="32eu3779ucnr9">
          <div className="flex items-center gap-2 mb-4" data-id="element-1859" data-mp="37a9dxqcjy3jg">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-1860" data-mp="3hf7z05uo997b">
              Performance Snapshot
            </h2>
            <span data-phoebe-tour="ai-insight-icon" className="inline-flex" data-id="element-1861" data-mp="3cfwgaraqvtn3">
            <AIInsightPopover align="left" headline="Participation is strong but completion is slipping" insightBeforeMetric="80% of learners were active this month, yet only" highlightedMetric="10%" insightAfterMetric=" have finished every module." explanation="The opportunity sits with active learners who have one or two modules left — a focused reminder should close the gap." insightId={12} data-id="element-1862" data-mp="37pofcxz4xbbe" />
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1863" data-mp="2kq9zybfw2p4c">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-1864" data-mp="myfj2r11h8fc">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1865" data-mp="sg0vagsbgzj5">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-1866" data-mp="xfwwk89mqcv0">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1867" data-mp="xdsm17h86flr">
                    55
                  </span>
                  <span className="align-middle text-sm" data-id="element-1868" data-mp="nhhaijpgltsd">
                    of your invited learners have
                    <br data-id="element-1869" data-mp="9ihh6yr04qpv" />
                    completed registration
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#007558] text-sm font-medium" data-id="element-1870" data-mp="ei2j9i1gxf6t">
                  <span className="text-xs" data-id="element-1871" data-mp="23vpf4za9xifq">▲</span>
                  <span data-id="element-1872" data-mp="23u4ohkr2yjn5">16% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-1873" data-mp="28szfvb345giv"></div>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-1874" data-mp="2iwmnt05we22y">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1875" data-mp="2wqdv01w5p9vi">
                <div className="flex items-start justify-between mb-3 gap-2" data-id="element-1876" data-mp="2wpvpeenltjax">
                  <div className="text-gray-700 leading-relaxed" data-id="element-1877" data-mp="3ap57xkza8pi2">
                    <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1878" data-mp="36oupv9y7u8mu">
                      {timePeriod === '7d' ? '65%' : '80%'}
                    </span>
                    <span className="align-middle text-sm" data-id="element-1879" data-mp="1gdytwsqnrpbx">
                      of your learners have
                      <br data-id="element-1880" data-mp="1ccwsc599r16s" />
                      spent time learning this{' '}
                      {timePeriod === '7d' ? 'week' : 'month'}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 flex-shrink-0" data-id="element-1881" data-mp="eno7fvr1i7al">
                    <div className="flex items-center bg-gray-100 rounded-md p-px" data-id="element-1882" data-mp="tdvhbn2zehzo">
                      <button onClick={() => setTimePeriod('7d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '7d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1883" data-mp="ogmvncimjvto">
                        7 Days
                      </button>
                      <button onClick={() => setTimePeriod('30d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '30d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1884" data-mp="5o10yvjgebao">
                        30 Days
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-sm font-medium ${timePeriod === '7d' ? 'text-[#007558]' : 'text-[#007558]'}`} data-id="element-1885" data-mp="1eq40r2si3iow">
                  <span className="text-xs" data-id="element-1886" data-mp="27e9m9rrdpbps">▲</span>
                  <span data-id="element-1887" data-mp="27eru7lneledj">
                    {timePeriod === '7d' ? '12% vs prior 7 days' : '20% vs 30 days ago'}
                  </span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-1888" data-mp="1xmcaojwgkhqj"></div>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-1889" data-mp="2qd463f5p92ct">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1890" data-mp="2vyd3rxn7clql">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-1891" data-mp="2vx1rf9foy5nv">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1892" data-mp="2qz05mgqvrs8p">
                    10%
                  </span>
                  <span className="align-middle text-sm" data-id="element-1893" data-mp="2m2rsta05ob4p">
                    of your learners have
                    <br data-id="element-1894" data-mp="2h85ila5t8om7" />
                    completed all skill modules
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#DB3725] text-sm font-medium" data-id="element-1895" data-mp="289k3xq3gltqp">
                  <span className="text-xs" data-id="element-1896" data-mp="23a7fj0wxwem0">▼</span>
                  <span data-id="element-1897" data-mp="239nz5omxtya3">20% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#DB3725]" data-id="element-1898" data-mp="1ycff1ii8bk67"></div>
            </motion.div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div data-phoebe-tour="navigation-cards" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-1899" data-mp="is6eyau5ttfz">
          {navigationCards.map((card, index) => {
          const Icon = card.icon;
          return <Link key={index} to={card.link} className="h-full" data-id="element-1900" data-mp="rt4kixk8y90o" data-mpi={index}>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + index * 0.1
            }} className="group flex h-full flex-col bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-1901" data-mp="3r7yictzkk32j">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-1902" data-mp="3cik5hbwsb3ie">
                    <Icon size={24} className="text-white" data-id="element-1903" data-mp="2og5j1zb86u9n" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#00638C] transition-colors" data-id="element-1904" data-mp="334s6kob0asby">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 flex-1" data-id="element-1905" data-mp="2ybp8q3z74wpy">
                    {card.description}
                  </p>
                  <div className="mt-auto flex items-center justify-between gap-2" data-id="element-1906" data-mp="376dfjykf6mgv">
                    <span className="text-xs font-medium text-gray-500" data-id="element-1907" data-mp="3m2uq4nksbx2d">
                      {card.stats}
                    </span>
                    <ArrowRight size={16} className="text-gray-400 group-hover:text-[#00638C] group-hover:translate-x-1 transition-all" data-id="element-1908" data-mp="3h52vhlsr9wuh" />
                  </div>
                </motion.div>
              </Link>;
        })}
        </div>

        {/* Contact Support - Full Width Horizontal */}
        <motion.div data-phoebe-tour="support" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="bg-gradient-to-br from-[#30718D] to-[#265a70] rounded-xl p-6 shadow-lg text-white mb-8" data-id="element-1909" data-mp="76t0238ci18x">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-id="element-1910" data-mp="3johdladkr3xy">
            <div className="flex items-center gap-4" data-id="element-1911" data-mp="35oyaivzmeisi">
              <div className="w-12 h-12 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-1912" data-mp="35qj3wia77vos">
                <HelpCircle size={24} className="text-white" data-id="element-1913" data-mp="13xbq5da8hm50" />
              </div>
              <div data-id="element-1914" data-mp="13v8plzbggam1">
                <h3 className="text-xl font-bold mb-1" data-id="element-1915" data-mp="18weibx547xvk">Need Help?</h3>
                <p className="text-sm text-white/70 max-w-2xl" data-id="element-1916" data-mp="18vmxnhd23kk2">
                  Our support team is available 24/7 to help you with any
                  questions or issues you may have.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end justify-center gap-3 w-full md:w-auto" data-id="element-1917" data-mp="xgjowzcoaz1e">
              <button className="w-full md:w-auto px-6 py-3 bg-white text-[#30718D] rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm font-semibold shadow-sm" data-id="element-1918" data-mp="siry68nrliys">
                <Mail size={16} data-id="element-1919" data-mp="1l7p2t8z1zh92" />
                Email Support
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}